import { useEscClose } from '../hooks/useEscClose';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, CheckCircle, Clock, FileText, BookOpen, 
  ChevronDown, ChevronRight, Eye, Check, X, Loader2,
  Video, Search, Download, Calendar, Unlock, MessageSquare, Star, PenTool, KeyRound
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Format minutes to human readable
const formatTime = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0) {
    return `${h}h ${m}m`;
  }
  return `${m} Min`;
};

/**
 * Admin Live Session Management
 * 
 * Allows admins/instructors to:
 * 1. See all participants and their progress per module
 * 2. View submitted tasks
 * 3. Approve live sessions to unlock next modules
 * 4. Export attendance lists and learning proofs
 */
const AdminLiveSessionManager = ({ courseId, courseName, getAuthHeader, onClose, includeArchived = false, initialExpandedUser = null }) => {
  const [participants, setParticipants] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(initialExpandedUser);
  const [selectedTaskView, setSelectedTaskView] = useState(null);
  const [taskData, setTaskData] = useState(null);
  const [feedbackForm, setFeedbackForm] = useState({});
  const [savingFeedback, setSavingFeedback] = useState({});
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [approveModal, setApproveModal] = useState(null); // {userId, moduleId, userName, moduleTitle}
  const [manualUnlockModal, setManualUnlockModal] = useState(null); // For offline learners
  useEscClose(() => { setSelectedTaskView(null); setApproveModal(null); setManualUnlockModal(null); });
  // Helper to get local date in YYYY-MM-DD format (avoiding timezone issues)
  const getLocalDateString = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [approveForm, setApproveForm] = useState({
    session_date: getLocalDateString(),
    session_duration_minutes: 45,
    session_topic: '',
    notes: '',
    instructor_signature: null
  });

  // Signature canvas reference
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const [manualUnlockForm, setManualUnlockForm] = useState({
    session_date: getLocalDateString(),
    session_duration_minutes: 45,
    notes: 'Offline-Lernen / Papier-Lernen'
  });

  // Load participants (server-paginiert, max. 25 pro Seite)
  const focusUserRef = useRef(initialExpandedUser);
  const loadParticipants = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), page_size: '25' });
      if (includeArchived) params.set('include_archived', 'true');
      if (searchQuery.trim()) params.set('search', searchQuery.trim());
      if (focusUserRef.current) { params.set('focus_user', focusUserRef.current); focusUserRef.current = null; }
      const res = await axios.get(`${API}/api/admin/live-sessions/${courseId}?${params.toString()}`, getAuthHeader());
      const data = res.data;
      setParticipants(data.participants || []);
      setTotalPages(data.total_pages || 0);
      setTotalUsers(data.total_users || 0);
      if (data.page && data.page !== page) setPage(data.page);
    } catch (error) {
      console.error('Error loading participants:', error);
      toast.error('Fehler beim Laden der Teilnehmer');
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, getAuthHeader, includeArchived, page, searchQuery]);

  useEffect(() => {
    loadParticipants();
  }, [loadParticipants]);

  // Debounced server-side Suche: Seite zurücksetzen (Reload via page/searchQuery-Deps)
  const searchDebounceRef = useRef(null);
  const onSearchChange = (value) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPage(1);
      setSearchQuery(value);
    }, 400);
  };

  // Wenn wir per Globaler Suche mit vorbelegtem User reingekommen sind:
  // nach dem Laden zu dessen Card scrollen.
  useEffect(() => {
    if (!initialExpandedUser || loading || participants.length === 0) return;
    const el = document.querySelector(`[data-participant-id="${initialExpandedUser}"]`);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    }
  }, [initialExpandedUser, loading, participants]);

  // View user's tasks for a module
  const viewTasks = async (userId, moduleId, userName, moduleTitle) => {
    setLoadingTasks(true);
    setSelectedTaskView({ userId, moduleId, userName, moduleTitle });
    try {
      const res = await axios.get(
        `${API}/api/admin/user-tasks/${userId}/${moduleId}`,
        getAuthHeader()
      );
      setTaskData(res.data);
    } catch (error) {
      console.error('Error loading tasks:', error);
      toast.error('Fehler beim Laden der Aufgaben');
    }
    setLoadingTasks(false);
  };

  // Save feedback for a task
  const saveFeedback = async (taskId) => {
    const form = feedbackForm[taskId];
    if (!form || !selectedTaskView) return;
    
    setSavingFeedback(prev => ({ ...prev, [taskId]: true }));
    
    try {
      await axios.post(
        `${API}/api/admin/task-feedback`,
        {
          user_id: selectedTaskView.userId,
          module_id: selectedTaskView.moduleId,
          task_id: taskId,
          feedback: form.feedback || '',
          rating: form.rating || 'bestanden'
        },
        getAuthHeader()
      );
      toast.success('Feedback gespeichert!');
      // Reload task data
      viewTasks(selectedTaskView.userId, selectedTaskView.moduleId, selectedTaskView.userName, selectedTaskView.moduleTitle);
    } catch (error) {
      console.error('Error saving feedback:', error);
      toast.error('Fehler beim Speichern');
    }
    setSavingFeedback(prev => ({ ...prev, [taskId]: false }));
  };

  // Export tasks for course
  const exportCourseTasks = async () => {
    try {
      const response = await axios.get(
        `${API}/api/admin/export-tasks/${courseId}`,
        {
          ...getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Aufgaben_${courseName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export erfolgreich!');
    } catch (error) {
      console.error('Error exporting tasks:', error);
      toast.error('Fehler beim Export');
    }
  };

  // Open approve modal
  const openApproveModal = (userId, moduleId, userName, moduleTitle) => {
    setApproveModal({ userId, moduleId, userName, moduleTitle });
    setApproveForm({
      session_date: getLocalDateString(),
      session_duration_minutes: 45,
      session_topic: moduleTitle,
      notes: '',
      instructor_signature: null
    });
    setHasSignature(false);
  };

  // Signature canvas functions
  const initSignatureCanvas = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#D4C4B0';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  };

  const startDrawing = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    setApproveForm(prev => ({ ...prev, instructor_signature: null }));
  };

  const getSignatureData = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas || !hasSignature) return null;
    return canvas.toDataURL('image/png');
  };

  // Approve live session with full details and optional signature
  const approveSession = async () => {
    if (!approveModal) return;
    
    const { userId, moduleId } = approveModal;
    const signatureData = getSignatureData();
    
    // If signature provided, use the new endpoint
    if (signatureData) {
      try {
        await axios.post(
          `${API}/api/admin/live-sessions/approve-with-signature`,
          {
            user_id: userId,
            module_id: moduleId,
            session_date: approveForm.session_date,
            session_duration_minutes: approveForm.session_duration_minutes,
            session_topic: approveForm.session_topic,
            notes: approveForm.notes,
            instructor_signature: signatureData
          },
          getAuthHeader()
        );
        toast.success('Live-Session mit Dozenten-Unterschrift bestätigt!');
        setApproveModal(null);
        loadParticipants();
        return;
      } catch (error) {
        console.error('Error approving with signature:', error);
        toast.error('Fehler beim Bestätigen');
        return;
      }
    }
    
    // Fallback to old endpoint without signature
    const params = new URLSearchParams({
      user_id: userId,
      module_id: moduleId,
      session_date: approveForm.session_date,
      session_duration_minutes: approveForm.session_duration_minutes.toString(),
      session_topic: approveForm.session_topic,
      notes: approveForm.notes
    });
    
    try {
      await axios.post(
        `${API}/api/admin/live-sessions/approve?${params.toString()}`,
        {},
        getAuthHeader()
      );
      toast.success('Live-Session bestätigt und dokumentiert!');
      setApproveModal(null);
      loadParticipants();
    } catch (error) {
      console.error('Error approving session:', error);
      toast.error('Fehler beim Bestätigen');
    }
  };

  // Revoke approval
  const revokeSession = async (userId, moduleId) => {
    try {
      await axios.post(
        `${API}/api/admin/live-sessions/revoke?user_id=${userId}&module_id=${moduleId}`,
        {},
        getAuthHeader()
      );
      toast.success('Bestätigung zurückgezogen');
      loadParticipants();
    } catch (error) {
      console.error('Error revoking session:', error);
      toast.error('Fehler beim Zurückziehen');
    }
  };

  // Open manual unlock modal (for offline learners)
  const openManualUnlockModal = (userId, moduleId, userName, moduleTitle) => {
    setManualUnlockModal({ userId, moduleId, userName, moduleTitle });
    setManualUnlockForm({
      session_date: getLocalDateString(),
      session_duration_minutes: 45,
      notes: 'Offline-Lernen / Papier-Lernen'
    });
  };

  // Manual unlock module (for offline learners) — Live-Session-Approval variant
  const manualUnlockModule = async () => {
    if (!manualUnlockModal) return;
    
    const { userId, moduleId } = manualUnlockModal;
    const params = new URLSearchParams({
      user_id: userId,
      module_id: moduleId,
      session_date: manualUnlockForm.session_date,
      session_duration_minutes: manualUnlockForm.session_duration_minutes.toString(),
      notes: manualUnlockForm.notes
    });
    
    try {
      const res = await axios.post(
        `${API}/api/admin/manual-unlock-module?${params.toString()}`,
        {},
        getAuthHeader()
      );
      toast.success(res.data.message || 'Modul erfolgreich freigeschaltet!');
      setManualUnlockModal(null);
      loadParticipants();
    } catch (error) {
      console.error('Error unlocking module:', error);
      toast.error('Fehler beim Freischalten');
    }
  };

  // Diagnose + Force-Unlock — for cases where a module is locked because the PREVIOUS one isn't done.
  // Shows admin exactly what's missing, then offers a 1-click override.
  const diagnoseAndForceUnlock = async (userId, moduleId, userName, moduleTitle) => {
    try {
      const res = await axios.get(
        `${API}/api/admin/users/${userId}/modules/${moduleId}/unlock-status`,
        getAuthHeader()
      );
      const d = res.data;
      // Build diagnosis text
      let diagnosis = `🔍 Status für „${moduleTitle}" (${userName}):\n\n`;
      diagnosis += `Aktuell: ${d.is_unlocked ? '✅ Offen' : '🔒 Gesperrt'}\n\n`;
      if (d.is_unlocked) {
        diagnosis += `Modul ist bereits zugänglich. Trotzdem freischalten? (markiert als Admin-Override)`;
      } else if (d.previous_module) {
        const pm = d.previous_module;
        diagnosis += `Vorgänger-Modul ${pm.prev_module_id} blockt:\n`;
        diagnosis += `  • Quiz: ${pm.quiz_passed ? `✅ ${pm.quiz_score}%` : `❌ ${pm.quiz_score}% (min. 70 nötig)`}\n`;
        diagnosis += `  • Aufgaben: ${pm.tasks_submitted_count} / ${pm.tasks_required_count} eingereicht\n`;
        if (pm.tasks_missing && pm.tasks_missing.length > 0) {
          diagnosis += `  • Fehlend: ${pm.tasks_missing.join(', ')}\n`;
        }
        diagnosis += `\nTrotzdem manuell freischalten? (Audit-Log wird angelegt)`;
      } else {
        diagnosis += `Kein klarer Grund — wahrscheinlich noch keine Daten. Trotzdem freischalten?`;
      }

      if (!window.confirm(diagnosis)) return;

      // Ask for reason
      const reason = window.prompt(
        'Begründung (für Audit-Log):',
        'Technische Probleme / Offline-Lernen / Sonderfreigabe'
      );
      if (reason === null) return;

      await axios.post(
        `${API}/api/admin/users/${userId}/modules/${moduleId}/force-unlock?reason=${encodeURIComponent(reason.trim() || 'Admin-Freischaltung')}`,
        {},
        getAuthHeader()
      );
      toast.success(`„${moduleTitle}" für ${userName} freigeschaltet`);
      loadParticipants();
    } catch (error) {
      console.error('diagnoseAndForceUnlock error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Freischalten');
    }
  };

  // Export functions
  const exportAttendance = async (format) => {
    try {
      const res = await axios.get(
        `${API}/api/admin/export/attendance/${courseId}?format=${format}`,
        { ...getAuthHeader(), responseType: 'blob' }
      );
      
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Anwesenheitsliste_${courseName.replace(/\s/g, '_')}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Anwesenheitsliste als ${format.toUpperCase()} heruntergeladen`);
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Fehler beim Export');
    }
  };

  const exportCertificateProof = async (userId, userName) => {
    try {
      const res = await axios.get(
        `${API}/api/admin/export/certificate-proof/${userId}/${courseId}`,
        { ...getAuthHeader(), responseType: 'blob' }
      );
      
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Lernzeitnachweis_${userName.replace(/\s/g, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Lernzeitnachweis heruntergeladen');
    } catch (error) {
      console.error('Error exporting proof:', error);
      toast.error('Fehler beim Export des Nachweises');
    }
  };

  // Serverseitig gefiltert & paginiert
  const filteredParticipants = participants;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-terra" />
      </div>
    );
  }

  // Task View Modal
  if (selectedTaskView && taskData) {
    return (
      <div className="bg-cream-100 rounded-2xl border border-cream-300 p-6 animate-fade-in">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-sand-800">Aufgaben von {selectedTaskView.userName}</h2>
            <p className="text-sm text-sand-500">{selectedTaskView.moduleTitle}</p>
            <p className="text-xs text-sand-600 mt-2">
              <span className="font-semibold">{taskData.tasks_submitted ?? 0} / {taskData.tasks_total ?? taskData.tasks.length}</span> Aufgaben eingereicht
              {(taskData.orphan_submissions?.length || 0) > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-coral-200 text-espresso font-semibold">
                  ⚠️ {taskData.orphan_submissions.length} verwaiste Antwort(en) in DB
                </span>
              )}
            </p>
          </div>
          <button
            onClick={() => { setSelectedTaskView(null); setTaskData(null); setFeedbackForm({}); }}
            className="p-2 rounded-lg bg-cream-200 hover:bg-cream-300 transition-colors"
          >
            <X className="w-5 h-5 text-sand-500" />
          </button>
        </div>

        {/* Orphan Submissions Banner — DB-Antworten ohne passendes Task-Def */}
        {(taskData.orphan_submissions?.length || 0) > 0 && (
          <div className="mb-6 p-4 bg-coral-100 border border-coral-200 rounded-xl">
            <div className="font-bold text-espresso mb-2">Verwaiste Antworten in der Datenbank</div>
            <p className="text-xs text-sand-700 mb-3">
              Diese Antworten wurden eingereicht, aber ihre Task-ID existiert nicht (mehr) in der aktuellen Modul-Definition.
              Wahrscheinlich wurde die Aufgabe umbenannt. Du kannst sie auf die richtige Task-ID umhängen — die Teilnehmerin
              muss <b>nichts</b> neu eintippen.
            </p>
            <div className="space-y-2">
              {taskData.orphan_submissions.map((o, i) => (
                <details key={i} className="bg-cream-50 rounded-lg p-3 border border-cream-300">
                  <summary className="cursor-pointer text-sm text-sand-800 flex items-center justify-between">
                    <span><b>task_id:</b> <code className="text-xs">{o.task_id}</code> · {o.word_count} Wörter · {o.submitted_at ? new Date(o.submitted_at).toLocaleDateString('de-DE') : '—'}</span>
                    <span className="text-xs text-sand-500">öffnen</span>
                  </summary>
                  <div className="mt-2">
                    <div className="text-[11px] text-sand-500 mb-1">Antwort-Text:</div>
                    <div className="text-xs text-sand-800 bg-cream-100 p-2 rounded whitespace-pre-wrap max-h-40 overflow-y-auto">{o.answer_text}</div>
                    <div className="mt-3 flex items-end gap-2">
                      <div className="flex-1">
                        <label className="block text-[11px] text-sand-600 mb-1">Auf welche Task-ID umhängen?</label>
                        <select
                          className="w-full px-2 py-1.5 bg-cream-50 border border-cream-300 rounded text-xs"
                          defaultValue=""
                          onChange={async (e) => {
                            const to = e.target.value;
                            if (!to) return;
                            if (!window.confirm(`Antwort von ${o.task_id} → ${to} umhängen?\nKein Datenverlust, die Aufgabe wird einfach der richtigen Task-ID zugeordnet.`)) {
                              e.target.value = ''; return;
                            }
                            try {
                              const params = new URLSearchParams({ module_id: selectedTaskView.moduleId, from_task_id: o.task_id, to_task_id: to });
                              await axios.post(
                                `${API}/api/admin/participant/${selectedTaskView.userId}/course/_/remap-task?${params}`,
                                {},
                                getAuthHeader()
                              );
                              toast.success('Antwort umgehängt!');
                              viewTasks(selectedTaskView.userId, selectedTaskView.moduleId, selectedTaskView.userName, selectedTaskView.moduleTitle);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Fehler beim Umhängen');
                              e.target.value = '';
                            }
                          }}
                        >
                          <option value="">— Task-ID auswählen —</option>
                          {taskData.tasks.filter(t => !t.submitted).map(t => (
                            <option key={t.task_id} value={t.task_id}>{t.task_id} — {t.title?.substring(0, 60)}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </details>
              ))}
            </div>
          </div>
        )}

        {loadingTasks ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-terra" />
          </div>
        ) : (
          <div className="space-y-6">
            {taskData.tasks.map((task, idx) => {
              const currentFeedback = feedbackForm[task.task_id] || {
                feedback: task.admin_feedback || '',
                rating: task.admin_rating || ''
              };
              
              return (
                <div key={idx} className="bg-cream-200 rounded-xl p-4 border border-cream-300">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-sand-800">{task.title}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          task.task_type === 'fallbeispiel' ? 'bg-blue-500/20 text-blue-400' :
                          task.task_type === 'reflexion' ? 'bg-purple-500/20 text-purple-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>
                          {task.task_type === 'fallbeispiel' ? 'Fallbeispiel' : 
                           task.task_type === 'reflexion' ? 'Reflexion' : 'Übung'}
                        </span>
                        {task.admin_rating && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            task.admin_rating === 'bestanden' ? 'bg-green-500/20 text-green-400' :
                            task.admin_rating === 'nicht_bestanden' ? 'bg-red-500/20 text-red-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>
                            {task.admin_rating === 'bestanden' ? '✓ Bestanden' : 
                             task.admin_rating === 'nicht_bestanden' ? '✗ Nicht bestanden' : '↻ Überarbeiten'}
                          </span>
                        )}
                      </div>
                    </div>
                    {task.submitted ? (
                      <div className="flex items-center gap-2">
                        {task.manually_added_by_admin && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-cream-300 text-sand-700 font-semibold uppercase">manuell nachgetragen</span>
                        )}
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-sand-500">Nicht eingereicht</span>
                        <button
                          onClick={async () => {
                            const note = window.prompt(
                              `Aufgabe „${task.title}" manuell als erledigt markieren.\n\n` +
                              `Was soll als Antwort gespeichert werden? (z.B. „Offline/handschriftlich bearbeitet", oder kopiere hier die Antwort der Teilnehmerin rein.)`,
                              'Offline/handschriftlich bearbeitet — vom Admin manuell nachgetragen'
                            );
                            if (!note || !note.trim()) return;
                            try {
                              const params = new URLSearchParams({ module_id: selectedTaskView.moduleId, task_id: task.task_id, note });
                              await axios.post(
                                `${API}/api/admin/participant/${selectedTaskView.userId}/course/_/manual-task-submission?${params}`,
                                {},
                                getAuthHeader()
                              );
                              toast.success('Aufgabe als erledigt markiert!');
                              viewTasks(selectedTaskView.userId, selectedTaskView.moduleId, selectedTaskView.userName, selectedTaskView.moduleTitle);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Fehler beim Markieren');
                            }
                          }}
                          className="text-[10px] px-2 py-1 rounded bg-sand-800 text-cream-50 hover:bg-espresso font-semibold"
                          data-testid={`manual-mark-done-${task.task_id}`}
                          title="Manuell als erledigt markieren (z.B. wenn offline/handschriftlich bearbeitet)"
                        >
                          Manuell erledigt
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Task Description */}
                  <div className="mb-4 p-3 bg-cream-50 rounded-lg">
                    <p className="text-sm text-sand-500 whitespace-pre-wrap">{task.description}</p>
                  </div>

                  {/* User's Answer */}
                  {task.submitted && task.answer_text ? (
                    <>
                      <div className="p-4 bg-cream-50 rounded-lg border border-cream-300 mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-terra">Antwort des Teilnehmers:</span>
                          <span className="text-xs text-sand-500">
                            {new Date(task.submitted_at).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                        <p className="text-sm text-sand-800 whitespace-pre-wrap leading-relaxed">
                          {task.answer_text}
                        </p>
                      </div>

                      {/* Feedback & Rating Section */}
                      <div className="p-4 bg-cream-50/50 rounded-lg border border-cream-300">
                        <div className="flex items-center gap-2 mb-3">
                          <MessageSquare className="w-4 h-4 text-terra" />
                          <span className="text-sm font-medium text-terra">Dozenten-Feedback</span>
                          {task.reviewed_by_name && (
                            <span className="text-xs text-sand-500">
                              (Bewertet von {task.reviewed_by_name})
                            </span>
                          )}
                        </div>
                        
                        {/* Rating Buttons */}
                        <div className="flex gap-2 mb-3">
                          <button
                            onClick={() => setFeedbackForm(prev => ({
                              ...prev,
                              [task.task_id]: { ...currentFeedback, rating: 'bestanden' }
                            }))}
                            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                              currentFeedback.rating === 'bestanden'
                                ? 'bg-green-500/30 text-green-400 border border-green-500/50'
                                : 'bg-cream-200 text-sand-500 hover:bg-cream-300'
                            }`}
                          >
                            ✓ Bestanden
                          </button>
                          <button
                            onClick={() => setFeedbackForm(prev => ({
                              ...prev,
                              [task.task_id]: { ...currentFeedback, rating: 'ueberarbeiten' }
                            }))}
                            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                              currentFeedback.rating === 'ueberarbeiten'
                                ? 'bg-amber-500/30 text-amber-400 border border-amber-500/50'
                                : 'bg-cream-200 text-sand-500 hover:bg-cream-300'
                            }`}
                          >
                            ↻ Überarbeiten
                          </button>
                          <button
                            onClick={() => setFeedbackForm(prev => ({
                              ...prev,
                              [task.task_id]: { ...currentFeedback, rating: 'nicht_bestanden' }
                            }))}
                            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                              currentFeedback.rating === 'nicht_bestanden'
                                ? 'bg-red-500/30 text-red-400 border border-red-500/50'
                                : 'bg-cream-200 text-sand-500 hover:bg-cream-300'
                            }`}
                          >
                            ✗ Nicht bestanden
                          </button>
                        </div>

                        {/* Feedback Textarea */}
                        <textarea
                          value={currentFeedback.feedback}
                          onChange={(e) => setFeedbackForm(prev => ({
                            ...prev,
                            [task.task_id]: { ...currentFeedback, feedback: e.target.value }
                          }))}
                          placeholder="Kommentar für den Teilnehmer..."
                          rows={3}
                          className="w-full px-3 py-2 bg-cream-200 border border-cream-300 rounded-lg text-sand-800 text-sm focus:outline-none focus:border-terra resize-none mb-3"
                        />

                        {/* Save Button */}
                        <button
                          onClick={() => saveFeedback(task.task_id)}
                          disabled={savingFeedback[task.task_id] || !currentFeedback.rating}
                          className="px-4 py-2 bg-gradient-to-r from-terra to-terra-dark rounded-lg text-sand-800 font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {savingFeedback[task.task_id] ? (
                            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                          ) : (
                            <Check className="w-4 h-4 inline mr-2" />
                          )}
                          Feedback speichern
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-sand-500 italic">Noch keine Antwort eingereicht</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Back Button */}
        <button
          onClick={() => { setSelectedTaskView(null); setTaskData(null); setFeedbackForm({}); }}
          className="mt-6 px-4 py-2 bg-cream-300 rounded-lg text-sand-800 hover:bg-cream-300 transition-colors"
        >
          Zurück zur Übersicht
        </button>
      </div>
    );
  }

  return (
    <div className="bg-cream-100 rounded-2xl border border-cream-300 p-6 animate-fade-in">
      {/* Approve Modal */}
      {approveModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-cream-100 rounded-2xl border border-cream-300 w-full max-w-md max-h-[90vh] flex flex-col animate-fade-in">
            {/* Sticky Header */}
            <div className="flex items-center justify-between p-6 border-b border-cream-300">
              <h3 className="text-lg font-bold text-sand-800">Live-Session dokumentieren</h3>
              <button onClick={() => setApproveModal(null)} className="p-1 hover:bg-cream-300 rounded">
                <X className="w-5 h-5 text-sand-500" />
              </button>
            </div>
            
            {/* Scrollable Body */}
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-sand-500 mb-1">Teilnehmer</p>
                  <p className="text-sm text-sand-800 font-medium">{approveModal.userName}</p>
                </div>
                <div>
                  <p className="text-xs text-sand-500 mb-1">Modul</p>
                  <p className="text-sm text-sand-800">{approveModal.moduleTitle}</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-sand-500 mb-2">Datum der Session *</label>
                <input
                  type="date"
                  value={approveForm.session_date}
                  onChange={(e) => setApproveForm({...approveForm, session_date: e.target.value})}
                  className="w-full px-4 py-3 bg-cream-200 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra"
                />
              </div>
              
              <div>
                <label className="block text-sm text-sand-500 mb-2">Dauer (Minuten) *</label>
                <input
                  type="number"
                  value={approveForm.session_duration_minutes}
                  onChange={(e) => setApproveForm({...approveForm, session_duration_minutes: parseInt(e.target.value) || 0})}
                  min="1"
                  className="w-full px-4 py-3 bg-cream-200 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra"
                />
              </div>
              
              <div>
                <label className="block text-sm text-sand-500 mb-2">Thema / Inhalt *</label>
                <input
                  type="text"
                  value={approveForm.session_topic}
                  onChange={(e) => setApproveForm({...approveForm, session_topic: e.target.value})}
                  placeholder="z.B. Reflexion Modul 1, Fallbesprechung..."
                  className="w-full px-4 py-3 bg-cream-200 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra"
                />
              </div>
              
              <div>
                <label className="block text-sm text-sand-500 mb-2">Notizen (optional)</label>
                <textarea
                  value={approveForm.notes}
                  onChange={(e) => setApproveForm({...approveForm, notes: e.target.value})}
                  placeholder="Weitere Anmerkungen..."
                  rows={2}
                  className="w-full px-4 py-3 bg-cream-200 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra resize-none"
                />
              </div>

              {/* Instructor Signature */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-sand-500 flex items-center gap-2">
                    <PenTool className="w-4 h-4" />
                    Dozenten-Unterschrift (optional)
                  </label>
                  {hasSignature && (
                    <button
                      type="button"
                      onClick={clearSignature}
                      className="text-xs text-red-400 hover:text-red-300"
                    >
                      Löschen
                    </button>
                  )}
                </div>
                <div className="border border-cream-300 rounded-xl bg-cream-200 overflow-hidden">
                  <canvas
                    ref={signatureCanvasRef}
                    width={360}
                    height={100}
                    className="w-full cursor-crosshair touch-none"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    onLoad={initSignatureCanvas}
                  />
                </div>
                <p className="text-xs text-sand-500 mt-1">
                  {hasSignature ? '✓ Unterschrift vorhanden' : 'Hier unterschreiben (mit Maus oder Finger)'}
                </p>
              </div>
            </div>
            
            {/* Sticky Footer */}
            <div className="flex gap-3 p-6 border-t border-cream-300 bg-cream-100">
              <button
                onClick={() => setApproveModal(null)}
                className="flex-1 px-4 py-3 bg-cream-300 rounded-xl text-sand-800 hover:bg-cream-300 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={approveSession}
                disabled={!approveForm.session_date || !approveForm.session_duration_minutes || !approveForm.session_topic}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-green-600 to-green-500 rounded-xl text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="w-4 h-4 inline mr-2" />
                {hasSignature ? 'Mit Unterschrift bestätigen' : 'Bestätigen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Unlock Modal (for offline learners) */}
      {manualUnlockModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-cream-100 rounded-2xl border border-cream-300 p-6 w-full max-w-md animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Unlock className="w-5 h-5 text-amber-400" />
                </div>
                <h3 className="text-lg font-bold text-sand-800">Modul manuell freischalten</h3>
              </div>
              <button onClick={() => setManualUnlockModal(null)} className="p-1 hover:bg-cream-300 rounded">
                <X className="w-5 h-5 text-sand-500" />
              </button>
            </div>
            
            {/* Info Box */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
              <p className="text-sm text-amber-200">
                Diese Funktion ist für Teilnehmer, die <strong>offline auf Papier</strong> gelernt haben. 
                Das Modul wird mit voller Lernzeit, Quiz bestanden und Live-Session als erledigt markiert.
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-sand-500 mb-1">Teilnehmer</p>
                <p className="text-sand-800 font-medium">{manualUnlockModal.userName}</p>
              </div>
              <div>
                <p className="text-sm text-sand-500 mb-1">Modul</p>
                <p className="text-sand-800">{manualUnlockModal.moduleTitle}</p>
              </div>
              
              <div>
                <label className="block text-sm text-sand-500 mb-2">Datum der Präsenz-Session *</label>
                <input
                  type="date"
                  value={manualUnlockForm.session_date}
                  onChange={(e) => setManualUnlockForm({...manualUnlockForm, session_date: e.target.value})}
                  className="w-full px-4 py-3 bg-cream-200 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra"
                />
              </div>
              
              <div>
                <label className="block text-sm text-sand-500 mb-2">Dauer der Präsenz (Minuten)</label>
                <input
                  type="number"
                  value={manualUnlockForm.session_duration_minutes}
                  onChange={(e) => setManualUnlockForm({...manualUnlockForm, session_duration_minutes: parseInt(e.target.value) || 0})}
                  min="1"
                  className="w-full px-4 py-3 bg-cream-200 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra"
                />
              </div>
              
              <div>
                <label className="block text-sm text-sand-500 mb-2">Bemerkung / Grund *</label>
                <textarea
                  value={manualUnlockForm.notes}
                  onChange={(e) => setManualUnlockForm({...manualUnlockForm, notes: e.target.value})}
                  placeholder="z.B. Offline-Lernen / Papier-Lernen, Präsenz am..."
                  rows={3}
                  className="w-full px-4 py-3 bg-cream-200 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra resize-none"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setManualUnlockModal(null)}
                className="flex-1 px-4 py-3 bg-cream-300 rounded-xl text-sand-800 hover:bg-cream-300 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={manualUnlockModule}
                disabled={!manualUnlockForm.session_date || !manualUnlockForm.notes}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-600 to-amber-500 rounded-xl text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Unlock className="w-4 h-4 inline mr-2" />
                Freischalten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-sand-800">Live-Session Verwaltung</h2>
          <p className="text-sm text-sand-500">{courseName}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Export Aufgaben Button */}
          <button
            onClick={exportCourseTasks}
            className="flex items-center gap-2 px-3 py-2 bg-purple-500/20 border border-purple-500/30 rounded-lg text-purple-400 hover:bg-purple-500/30 transition-colors text-sm"
            title="Alle Aufgaben-Antworten als Excel exportieren"
          >
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Aufgaben</span>
          </button>
          {/* Export Buttons */}
          <button
            onClick={() => exportAttendance('pdf')}
            className="flex items-center gap-2 px-3 py-2 bg-cream-200 border border-cream-300 rounded-lg text-sand-800 hover:bg-cream-300 transition-colors text-sm"
            title="Anwesenheitsliste als PDF"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">PDF</span>
          </button>
          <button
            onClick={() => exportAttendance('excel')}
            className="flex items-center gap-2 px-3 py-2 bg-cream-200 border border-cream-300 rounded-lg text-sand-800 hover:bg-cream-300 transition-colors text-sm"
            title="Anwesenheitsliste als Excel"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Excel</span>
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-cream-200 hover:bg-cream-300 transition-colors"
            >
              <X className="w-5 h-5 text-sand-500" />
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-sand-500" />
        <input
          type="text"
          defaultValue={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Teilnehmer suchen..."
          data-testid="lsm-search-input"
          className="w-full pl-10 pr-4 py-3 bg-cream-200 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra"
        />
      </div>

      {/* Participants List */}
      {filteredParticipants.length === 0 ? (
        <div className="text-center py-8 text-sand-500">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Keine Teilnehmer gefunden</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredParticipants.map((participant) => (
            <div key={participant.user_id} data-participant-id={participant.user_id} className={`bg-cream-200 rounded-xl border border-cream-300 overflow-hidden ${participant.archived ? 'opacity-70' : ''} ${expandedUser === participant.user_id ? 'ring-2 ring-terra' : ''}`}>
              {/* Participant Header */}
              <div
                className="p-4 cursor-pointer flex items-center justify-between hover:bg-cream-200 transition-colors"
                onClick={() => setExpandedUser(expandedUser === participant.user_id ? null : participant.user_id)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-cream-50 font-bold ${participant.archived ? 'bg-sand-500' : 'bg-gradient-to-br from-terra to-terra-dark'}`}>
                    {participant.user_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="font-medium text-sand-800 flex items-center gap-2 flex-wrap">
                      {participant.user_name}
                      {participant.archived && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sand-800 text-cream-50 text-[10px] font-bold tracking-wide uppercase"
                          title={participant.archived_at ? `Archiviert am ${new Date(participant.archived_at).toLocaleDateString('de-DE')}${participant.archived_by ? ` durch ${participant.archived_by}` : ''}` : 'Archiviert'}
                          data-testid={`archived-badge-${participant.user_id}`}
                        >
                          Archiviert
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-sand-500">@{participant.username}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {/* Quick Stats */}
                  <div className="hidden md:flex items-center gap-4 text-sm">
                    <span className="text-sand-500">
                      {participant.modules?.filter(m => m.live_session_approved).length || 0} / {participant.modules?.length || 0} Sessions
                    </span>
                  </div>
                  {/* Export individual proof button */}
                  <button
                    onClick={(e) => { e.stopPropagation(); exportCertificateProof(participant.user_id, participant.user_name); }}
                    className="p-2 rounded-lg bg-cream-300 hover:bg-cream-300 transition-colors"
                    title="Lernzeitnachweis exportieren"
                  >
                    <FileText className="w-4 h-4 text-terra" />
                  </button>
                  {expandedUser === participant.user_id ? (
                    <ChevronDown className="w-5 h-5 text-sand-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-sand-500" />
                  )}
                </div>
              </div>

              {/* Expanded Modules */}
              {expandedUser === participant.user_id && (
                <div className="border-t border-cream-300 p-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-sand-500">
                          <th className="pb-3 font-medium">Modul</th>
                          <th className="pb-3 font-medium text-center">
                            <span
                              title="Anerkannte UE-Stunden gem. AZAV. 1 UE = 45 min. Wird automatisch beim Live-Session-Approval gutgeschrieben (Modul-Soll). Beinhaltet Live-Session, Selbststudium, Aufgaben und Praxistransfer."
                              className="cursor-help underline decoration-dotted decoration-sand-400 underline-offset-2"
                            >
                              Anerkannte UE
                            </span>
                          </th>
                          <th className="pb-3 font-medium text-center">Aufgaben</th>
                          <th className="pb-3 font-medium text-center">Quiz</th>
                          <th className="pb-3 font-medium text-center">Live-Session</th>
                          <th className="pb-3 font-medium text-center">Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {participant.modules?.map((mod, idx) => (
                          <tr key={idx} className="border-t border-cream-300">
                            <td className="py-3">
                              <div>
                                <p className="text-sand-800">Modul {mod.module_order}</p>
                                <p className="text-xs text-sand-500 truncate max-w-[200px]">{mod.module_title}</p>
                              </div>
                            </td>
                            <td className="py-3 text-center">
                              <span className={mod.time_completed ? 'text-green-400' : 'text-sand-500'}>
                                {formatTime(mod.time_accumulated_minutes)} / {formatTime(mod.time_required_minutes)}
                              </span>
                            </td>
                            <td className="py-3 text-center">
                              <span className={mod.tasks_submitted >= mod.tasks_total && mod.tasks_total > 0 ? 'text-green-400' : 'text-sand-500'}>
                                {mod.tasks_submitted} / {mod.tasks_total}
                              </span>
                            </td>
                            <td className="py-3 text-center">
                              {mod.quiz_passed ? (
                                <span className="text-green-400">{mod.quiz_score}%</span>
                              ) : (
                                <span className="text-sand-500">-</span>
                              )}
                            </td>
                            <td className="py-3 text-center">
                              {mod.live_session_approved ? (
                                <CheckCircle className="w-5 h-5 text-green-400 mx-auto" />
                              ) : (
                                <span className="text-sand-500">Ausstehend</span>
                              )}
                            </td>
                            <td className="py-3">
                              <div className="flex items-center justify-center gap-2">
                                {/* View Tasks */}
                                <button
                                  onClick={() => viewTasks(participant.user_id, mod.module_id, participant.user_name, mod.module_title)}
                                  className="p-2 rounded-lg bg-cream-300 hover:bg-cream-300 transition-colors"
                                  title="Aufgaben ansehen"
                                >
                                  <Eye className="w-4 h-4 text-terra" />
                                </button>

                                {/* Force-unlock module (override prerequisites) */}
                                <button
                                  onClick={() => diagnoseAndForceUnlock(participant.user_id, mod.module_id, participant.user_name, mod.module_title)}
                                  className="p-2 rounded-lg bg-sage/15 hover:bg-sage/30 transition-colors"
                                  title="Modul freischalten / Diagnose (überspringt Vorgänger-Modul-Sperre)"
                                  data-testid={`force-unlock-${participant.user_id}-${mod.module_id}`}
                                >
                                  <KeyRound className="w-4 h-4 text-sage-dark" />
                                </button>
                                
                                {/* Manual Unlock Button (for offline learners) */}
                                {!mod.live_session_approved && (
                                  <button
                                    onClick={() => openManualUnlockModal(participant.user_id, mod.module_id, participant.user_name, mod.module_title)}
                                    className="p-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 transition-colors"
                                    title="Manuell freischalten (Offline-Lerner)"
                                  >
                                    <Unlock className="w-4 h-4 text-amber-400" />
                                  </button>
                                )}
                                
                                {/* Approve/Revoke Session */}
                                {mod.live_session_approved ? (
                                  <button
                                    onClick={() => revokeSession(participant.user_id, mod.module_id)}
                                    className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 transition-colors"
                                    title="Bestätigung zurückziehen"
                                  >
                                    <X className="w-4 h-4 text-red-400" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => {
                                      if (mod.quiz_passed) {
                                        openApproveModal(participant.user_id, mod.module_id, participant.user_name, mod.module_title);
                                      } else {
                                        toast.error('Quiz muss zuerst bestanden sein (≥70%)');
                                      }
                                    }}
                                    disabled={!mod.quiz_passed}
                                    className={`p-2 rounded-lg transition-colors ${
                                      mod.quiz_passed
                                        ? 'bg-green-500/20 hover:bg-green-500/30'
                                        : 'bg-cream-300 cursor-not-allowed opacity-50'
                                    }`}
                                    title={mod.quiz_passed ? 'Session bestätigen' : 'Quiz noch nicht bestanden'}
                                  >
                                    <Check className="w-4 h-4 text-green-400" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6" data-testid="lsm-pagination">
          <button
            onClick={() => { setLoading(true); setPage(p => Math.max(1, p - 1)); }}
            disabled={page <= 1}
            className="px-4 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider border border-terra text-terra hover:bg-terra hover:text-cream-50 transition-colors disabled:opacity-40 disabled:pointer-events-none"
            data-testid="lsm-prev-page"
          >
            ← Zurück
          </button>
          <span className="text-[13px] text-sand-600">
            Seite {page} von {totalPages} · {totalUsers} Teilnehmer
          </span>
          <button
            onClick={() => { setLoading(true); setPage(p => Math.min(totalPages, p + 1)); }}
            disabled={page >= totalPages}
            className="px-4 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider border border-terra text-terra hover:bg-terra hover:text-cream-50 transition-colors disabled:opacity-40 disabled:pointer-events-none"
            data-testid="lsm-next-page"
          >
            Weiter →
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminLiveSessionManager;
